import { useState, useCallback } from 'react';
import { message } from 'antd';
import { m302Api, M302ReportRow, M302SubtotalRow, M302GrandTotalRow, M302ReportParams } from '../../../services/m302Api';

/**
 * Fills the "last" (end) value based on "start" + padding with 'z'.
 * BR-001: warehouse 5 chars, BR-002: product 12 chars, BR-003: batch 12 chars
 */
function autoFillEnd(start: string, maxLen: number): string {
  const trimmed = start.trim();
  if (!trimmed) {
    return 'z'.repeat(maxLen);
  }
  const padded = trimmed + 'z'.repeat(maxLen);
  return padded.substring(0, maxLen);
}

export function useM302Query() {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<M302ReportRow[]>([]);
  const [subtotals, setSubtotals] = useState<M302SubtotalRow[]>([]);
  const [grandTotals, setGrandTotals] = useState<M302GrandTotalRow[]>([]);
  const [total, setTotal] = useState(0);

  // Filter state
  const [startWarehouse, setStartWarehouse] = useState('');
  const [lastWarehouse, setLastWarehouse]   = useState('');
  const [startProduct, setStartProduct]     = useState('');
  const [lastProduct, setLastProduct]       = useState('');
  const [startBatch, setStartBatch]         = useState('');
  const [lastBatch, setLastBatch]           = useState('');
  const [startDate, setStartDate]           = useState('');
  const [lastDate, setLastDate]             = useState('');
  const [excludeZero, setExcludeZero]       = useState(true); // BR-005: default exclude zero

  // BR-001: Auto-fill lastWarehouse when startWarehouse changes
  const handleStartWarehouseBlur = useCallback(() => {
    setLastWarehouse(autoFillEnd(startWarehouse, 5));
  }, [startWarehouse]);

  // BR-002: Auto-fill lastProduct when startProduct changes
  const handleStartProductBlur = useCallback(() => {
    setLastProduct(autoFillEnd(startProduct, 12));
  }, [startProduct]);

  // BR-003: Auto-fill lastBatch when startBatch changes
  const handleStartBatchBlur = useCallback(() => {
    setLastBatch(autoFillEnd(startBatch, 12));
  }, [startBatch]);

  const handleQuery = useCallback(async () => {
    setLoading(true);
    try {
      const params: M302ReportParams = {
        startWarehouse: startWarehouse || undefined,
        lastWarehouse:  lastWarehouse  || undefined,
        startProduct:   startProduct   || undefined,
        lastProduct:    lastProduct    || undefined,
        startBatch:     startBatch     || undefined,
        lastBatch:      lastBatch      || undefined,
        startDate:      startDate      || undefined,
        lastDate:       lastDate       || undefined,
        excludeZero,
      };
      const res = await m302Api.query(params);
      setRecords(res.items || []);
      setSubtotals(res.subtotals || []);
      setGrandTotals(res.grandTotals || []);
      setTotal(res.total || 0);
      if ((res.total || 0) === 0) {
        message.info('查無此資料!');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const errorMessage = axiosErr?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(errorMessage);
      setRecords([]);
      setSubtotals([]);
      setGrandTotals([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [startWarehouse, lastWarehouse, startProduct, lastProduct, startBatch, lastBatch, startDate, lastDate, excludeZero]);

  return {
    loading, records, subtotals, grandTotals, total,
    startWarehouse, setStartWarehouse, handleStartWarehouseBlur,
    lastWarehouse, setLastWarehouse,
    startProduct, setStartProduct, handleStartProductBlur,
    lastProduct, setLastProduct,
    startBatch, setStartBatch, handleStartBatchBlur,
    lastBatch, setLastBatch,
    startDate, setStartDate,
    lastDate, setLastDate,
    excludeZero, setExcludeZero,
    handleQuery,
  };
}
